/**
 * Project-asset phase grouping for the timeline sidebar.
 * Explicit `phase` on the asset is used when set; otherwise names are matched
 * (case-insensitive) for default campaign assets. Unknown names go to main phase.
 *
 * Phase colors are defined in app/assets/stylesheets/application.tailwind.css
 * (.timeline-phase-* classes).
 */

export const PROJECT_ASSET_PHASES = [
  {
    id: 'start',
    label: 'Start Phase',
    assetNames: ['Selection', 'Contact', 'Negotiations', 'Contract Signing'],
  },
  {
    id: 'main',
    label: 'Main Phase',
    assetNames: ['Draft Phase', 'Rework Phase', 'Go-Live Phase', 'Ads'],
  },
  {
    id: 'end',
    label: 'End Phase',
    assetNames: ['Send Insight', 'Send Invoice'],
  },
]

export const PROJECT_ASSET_PHASE_IDS = PROJECT_ASSET_PHASES.map((phase) => phase.id)

export const PROJECT_ASSET_PHASE_OPTIONS = PROJECT_ASSET_PHASES.map(({ id, label }) => ({
  value: id,
  label,
}))

const normalizeName = (name) => (name || '').trim().toLowerCase()

const DEFAULT_PROJECT_ASSET_NAMES = new Set(
  PROJECT_ASSET_PHASES.flatMap((phase) => phase.assetNames).map(normalizeName),
)

/** Manually created assets store phase; seeded defaults use name-only matching (phase null). */
export function isCustomProjectAsset(asset) {
  if (asset?.phase) return true
  return !DEFAULT_PROJECT_ASSET_NAMES.has(normalizeName(asset?.name))
}

const nameToPhaseId = (() => {
  const map = new Map()
  PROJECT_ASSET_PHASES.forEach((phase) => {
    phase.assetNames.forEach((assetName) => {
      map.set(normalizeName(assetName), phase.id)
    })
  })
  return map
})()

const isValidPhaseId = (phaseId) => PROJECT_ASSET_PHASE_IDS.includes(phaseId)

export function projectAssetPhaseId(asset) {
  if (asset?.phase && isValidPhaseId(asset.phase)) return asset.phase
  return nameToPhaseId.get(normalizeName(asset?.name)) || 'main'
}

export function groupProjectAssetsByPhase(projectAssets = []) {
  const buckets = Object.fromEntries(PROJECT_ASSET_PHASES.map((phase) => [phase.id, []]))

  projectAssets.forEach((asset) => {
    const phaseId = projectAssetPhaseId(asset)
    buckets[phaseId].push(asset)
  })

  return PROJECT_ASSET_PHASES.map((phase) => ({
    ...phase,
    assets: buckets[phase.id],
  }))
}
